import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Users, Car, Clock, ChevronDown, ChevronRight, Plus, MapPin, Smartphone, AlertCircle, DollarSign, CheckCircle, Phone, Mail } from 'lucide-react';
import { Customer, CustomerVehicle, getCustomer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import { usePermissions } from '../contexts/PermissionContext';
import { createMobileBooking } from '../lib/bookings';
import SelectResourceModal from './manage/SelectResourceModal';
import EditVehicleModal from './booking/EditVehicleModal';
import EditCustomerModal from './manage/EditCustomerModal';
import { Service, ServiceAddon } from '../lib/services';
import SelectCustomerModal from './manage/SelectCustomerModal';
import EditServiceModal from './booking/EditServiceModal';
import CreateVehicleModal from './CreateVehicleModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import SelectServiceModal from './manage/SelectServiceModal';

import { supabase } from '../lib/supabase';
import SelectServiceAddonsModal from './manage/SelectServiceAddonsModal';
import { format, getDaysInMonth } from 'date-fns';
import AddressAutocompleteModal from './AddressAutocompleteModal';
// Removed all persistence systems
import { triggerBookingAutomations } from '../lib/automationsV2';
import { cancelBookingAutomations } from '../services/automationCancellationService';
import { CustomerSourcesService, CustomerSource } from '../lib/customerSources';
import { getWorkflowByServiceId, getWorkflowSteps } from '../lib/workflows';

// Update Service interface to include duration_minutes
interface ExtendedService extends Service {
  duration_minutes?: number;
  tax_rate?: number;
  tax_exempt?: boolean;
}

interface CreateMobileBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSwitchToLocation?: () => void;
  // Add reschedule props
  isReschedule?: boolean;
  existingBookingId?: string;
  prefilledData?: {
    customer?: any;
    services?: Array<{
      vehicle: CustomerVehicle;
      service: ExtendedService;
      priceOption: any;
    }>;
    scheduleData?: {
      dropOffDate: string;
      dropOffTime: string;
      startDate: string;
      startTime: string;
      endDate: string;
      endTime: string;
      customer_notes: string;
      availableHours?: number;
    };
    bookingAddress?: string;
    bookingColor?: string;
    bookingSecondaryColor?: string;
    bookingSource?: string;
    // Quick booking specific data
    resource?: {
      id: string;
      name: string;
      resource_id: string;
    };
    isQuickBooking?: boolean;
  };
}

// Update the interfaces to match the database structure
interface Shift {
  start: string;
  end: string;
}

interface DaySchedule {
  radius: number;
  shifts: Shift[];
  enabled: boolean;
  postal_codes?: string[]; // Array of postal codes that can be serviced on this day
}

interface WorkingHours {
  [day: string]: DaySchedule;
}

// Interface for mobile availability
interface MobileAvailability {
  id: string;
  resource_id: string;
  business_id: string;
  mobile_available: boolean;
  working_hours: WorkingHours;
  postal_codes: {
    [day: string]: string[];
  };
  created_at: string;
  updated_at: string;
}

// Update the Resource interface
interface Resource {
  id: string;           // This is the mobile_availability record ID
  resource_id: string;  // This is the ID from the resources table
  name: string;
  business_id: string;
  mobile_available: boolean;
  working_hours: WorkingHours;
  postal_codes: {
    [day: string]: string[];
  };
  created_at?: string;
  updated_at?: string;
}

// Add interface for the resources query response
interface ResourceQueryResponse {
  id: string;
  resource_id: string;
  resources: {
    id: string;
    name: string;
  };
}

// Add new interface for blocked time slots
interface BlockedTimeSlot {
  date: string;
  blockedSlots: string[];
}

interface BlockedTimeSlots {
  [resourceId: string]: BlockedTimeSlot[];
}

// Helper function to get days in month with proper day offset
const getDaysInMonthArray = (date: Date) => {
  // Create date objects for first and last day of month, using noon to avoid timezone issues
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1, 12, 0, 0);
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0, 12, 0, 0);
  const days = [];
  
  // Get the day of week (0-6, where 0 is Sunday)
  let startingDay = firstDay.getDay();
  // Convert to Monday-based week (0-6, where 0 is Monday)
  startingDay = startingDay === 0 ? 6 : startingDay - 1;
  
  // Add empty days for padding at start
  for (let i = 0; i < startingDay; i++) {
    days.push(null);
  }
  
  // Add actual days
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(i);
  }
  
  return days;
};

export default function CreateMobileBookingModal({
  isOpen,
  onClose,
  onSuccess,
  onSwitchToLocation,
  isReschedule = false,
  existingBookingId,
  prefilledData
}: CreateMobileBookingModalProps) {
  const { selectedBusiness } = useBusiness();
  const { currentResource } = usePermissions();
  const [isAnimating, setIsAnimating] = useState(false);
  const [title, setTitle] = useState('Mobile Booking');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedServices, setSelectedServices] = useState<Array<{
    vehicle: CustomerVehicle;
    service: ExtendedService;
    priceOption: any;
  }>>([]);
  const [currentVehicle, setCurrentVehicle] = useState<CustomerVehicle | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [selectedServiceToEdit, setSelectedServiceToEdit] = useState<any>(null);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [selectedVehicleToEdit, setSelectedVehicleToEdit] = useState<CustomerVehicle | null>(null);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  
  // Debug effect to track modal state changes
  useEffect(() => {
    console.log('🔍 DEBUG: isSelectServiceModalOpen changed to:', isSelectServiceModalOpen);
  }, [isSelectServiceModalOpen]);
  const [scheduleData, setScheduleData] = useState({
    dropOffDate: '',
    dropOffTime: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    customer_notes: ''
  });
  const [bookingColor, setBookingColor] = useState('#3b82f6'); // Default blue
  const [bookingSecondaryColor, setBookingSecondaryColor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSelectAddonsModalOpen, setIsSelectAddonsModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState<ExtendedService | null>(null);
  const [selectedServiceAddons, setSelectedServiceAddons] = useState<Record<string, ServiceAddon[]>>({});
  const [mobileAvailability, setMobileAvailability] = useState<MobileAvailability[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [postalCode, setPostalCode] = useState<string>('');
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isResourceSelectorOpen, setIsResourceSelectorOpen] = useState(false);
  const [availableResources, setAvailableResources] = useState<Resource[]>([]);
  const [resourceNames, setResourceNames] = useState<Record<string, string>>({});
  const [bookingAddress, setBookingAddress] = useState<string>('');
  const [blockedTimeSlots, setBlockedTimeSlots] = useState<BlockedTimeSlots>({});
  const [blockedSlotsLoading, setBlockedSlotsLoading] = useState(false);

  // Initialize with pre-filled data when in reschedule mode or quick booking mode
  useEffect(() => {
    const loadRescheduleData = async () => {
      if (prefilledData && isOpen) {
        if (isReschedule) {
          setTitle('Reschedule Mobile Booking');
          setSelectedCustomer(prefilledData.customer || null);
          setSelectedServices(prefilledData.services || []);
          setScheduleData(prefilledData.scheduleData || {
            dropOffDate: '',
            dropOffTime: '',
            startDate: '',
            startTime: '',
            endDate: '',
            endTime: '',
            customer_notes: ''
          });
          setBookingAddress(prefilledData.bookingAddress || '');
          setBookingColor(prefilledData.bookingColor || '#3b82f6');
          setBookingSecondaryColor(prefilledData.bookingSecondaryColor || null);
          
          // Set customer source from prefilled booking data if available
          if (prefilledData.bookingSource && selectedBusiness) {
            try {
              // Find the matching customer source from our business sources
              const customerSources = await CustomerSourcesService.getCustomerSources(selectedBusiness.id);
              const matchingSource = customerSources.find(source => source.name === prefilledData.bookingSource);
              if (matchingSource) {
                setSelectedCustomerSource(matchingSource);
              }
            } catch (error) {
              console.error('Error loading customer sources for reschedule:', error);
            }
          }
        
        // Extract postal code from booking address if available
        if (prefilledData.bookingAddress) {
          console.log('🔍 Reschedule: Extracting postal code from address:', prefilledData.bookingAddress);
          
          // Enhanced regex patterns for different postal code formats
          const canadianPostalPattern = /[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d/; // Canadian: A1A 1A1
          const usZipPattern = /\d{5}(-\d{4})?/; // US: 12345 or 12345-6789
          
          let postalMatch = prefilledData.bookingAddress.match(canadianPostalPattern);
          if (!postalMatch) {
            postalMatch = prefilledData.bookingAddress.match(usZipPattern);
          }
          
          if (postalMatch) {
            const extractedPostal = postalMatch[0].toUpperCase().replace(/\s+/g, '');
            console.log('✅ Reschedule: Extracted postal code:', extractedPostal);
            setPostalCode(extractedPostal);
          } else {
            console.log('❌ Reschedule: No postal code found in address');
            // As a fallback, try to extract any alphanumeric pattern that might be a postal code
            const fallbackPattern = /[A-Z0-9]{5,7}/gi;
            const fallbackMatch = prefilledData.bookingAddress.match(fallbackPattern);
            if (fallbackMatch && fallbackMatch.length > 0) {
              const fallbackPostal = fallbackMatch[fallbackMatch.length - 1].toUpperCase();
              console.log('🔄 Reschedule: Using fallback postal code:', fallbackPostal);
              setPostalCode(fallbackPostal);
            }
          }
        } else {
          console.log('❌ Reschedule: No booking address provided');
        }
      } else if (prefilledData.isQuickBooking) {
        setTitle('Quick Booking');
        // Set pre-selected resource for quick booking
        if (prefilledData.resource) {
          setSelectedResource({
            id: prefilledData.resource.id,
            resource_id: prefilledData.resource.resource_id,
            name: prefilledData.resource.name,
            business_id: selectedBusiness?.id || '',
            mobile_available: true,
            working_hours: {},
            postal_codes: {}
          });
        }
        // Set pre-selected schedule data
        if (prefilledData.scheduleData) {
          setScheduleData(prefilledData.scheduleData);
        }
        // Don't reset selectedServices in quick booking mode - let the saved data load
        }
      } else if (!isOpen) {
        // Reset state when modal closes
        setTitle('Mobile Booking');
        setSelectedCustomer(null);
        setSelectedServices([]);
        setScheduleData({
          dropOffDate: '',
          dropOffTime: '',
          startDate: '',
          startTime: '',
          endDate: '',
          endTime: '',
          customer_notes: ''
        });
        setBookingAddress('');
        setBookingColor('#3b82f6');
        setBookingSecondaryColor(null);
        setPostalCode('');
        setError(null);
        setSelectedResource(null);
        setSelectedCustomerSource(null);
        setIsCustomerSourceSelectionModalOpen(false);
        setIsNotesExpanded(false);
      }
    };
    
    loadRescheduleData();
  }, [isReschedule, prefilledData, isOpen, selectedBusiness?.id]);
  const [blockedSlotsError, setBlockedSlotsError] = useState<string | null>(null);
  const [bufferTime, setBufferTime] = useState<number>(30); // Default buffer time (30 minutes)
  const [bufferTimeLoading, setBufferTimeLoading] = useState(false);

  // Customer Source state
  const [customerSources, setCustomerSources] = useState<CustomerSource[]>([]);
  const [selectedCustomerSource, setSelectedCustomerSource] = useState<CustomerSource | null>(null);
  const [isCustomerSourceDropdownOpen, setIsCustomerSourceDropdownOpen] = useState(false);
  const [isCreateSourceModalOpen, setIsCreateSourceModalOpen] = useState(false);
  
  // Workflow state
  const [servicesWithWorkflows, setServicesWithWorkflows] = useState<Set<string>>(new Set());
  const [workflowEnabled, setWorkflowEnabled] = useState(true); // Default enabled if workflows exist
  const [newSourceName, setNewSourceName] = useState('');
  const [createSourceLoading, setCreateSourceLoading] = useState(false);
  
  // New customer source selection modal state
  const [isCustomerSourceSelectionModalOpen, setIsCustomerSourceSelectionModalOpen] = useState(false);
  // Inline customer source dropdown state
  const [showCustomerSourceDropdown, setShowCustomerSourceDropdown] = useState(false);
  
  // Notes section expanded state
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  
  // Auto-expand notes if there are existing notes
  useEffect(() => {
    if (scheduleData.customer_notes && scheduleData.customer_notes.trim()) {
      setIsNotesExpanded(true);
    }
  }, [scheduleData.customer_notes]);

  // Helper functions for readable date/time formatting
  const formatReadableDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      // Handle different date formats and avoid timezone issues
      let date: Date;
      
      // If it's in YYYY-MM-DD format, parse it as local date to avoid timezone shift
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        date = new Date(dateString);
      }
      
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const dayName = dayNames[date.getDay()];
      const monthName = monthNames[date.getMonth()];
      const day = date.getDate();
      
      // Add ordinal suffix (st, nd, rd, th)
      const getOrdinalSuffix = (day: number) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      };
      
      return `${dayName}, ${monthName} ${day}${getOrdinalSuffix(day)}`;
    } catch (error) {
      return dateString; // Fallback to original if parsing fails
    }
  };

  const formatReadableTime = (timeString: string) => {
    if (!timeString) return '';
    try {
      // Parse time string (assuming format like "09:00" or "13:30")
      const [hours, minutes] = timeString.split(':').map(Number);
      
      if (hours === 12) {
        return minutes === 0 ? '12pm' : `12:${minutes.toString().padStart(2, '0')}pm`;
      } else if (hours === 0) {
        return minutes === 0 ? '12am' : `12:${minutes.toString().padStart(2, '0')}am`;
      } else if (hours < 12) {
        return minutes === 0 ? `${hours}am` : `${hours}:${minutes.toString().padStart(2, '0')}am`;
      } else {
        const hour12 = hours - 12;
        return minutes === 0 ? `${hour12}pm` : `${hour12}:${minutes.toString().padStart(2, '0')}pm`;
      }
    } catch (error) {
      return timeString; // Fallback to original if parsing fails
    }
  };
  
  // Ref for customer source dropdown positioning
  const customerSourceButtonRef = useRef<HTMLButtonElement>(null);

      // Removed all persistence systems
  // No need for duplicate modal persistence systems

  // Helper function to save current form state with optional overrides
  const saveMobileBookingProgress = (overrides: Record<string, any> = {}) => {
    const formData = {
      title,
      selectedCustomer,
      selectedServices,
      scheduleData,
      bookingColor,
      bookingSecondaryColor,
      selectedServiceAddons,
      bookingAddress,
      postalCode,
      selectedResource,
      ...overrides // Apply any immediate overrides to handle state timing issues
    };
    console.log('💾 Saving CreateMobileBookingModal progress:', Object.keys(formData));
    // Note: Persistence now handled centrally by usePageLifecycle
  };

  // Load saved data when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📥 Loading CreateMobileBookingModal progress...');
      // Note: Persistence now handled centrally by usePageLifecycle
      console.log('📥 Progress loading handled by centralized system');
    }
  }, [isOpen]);

  // Calculate total duration in minutes
  const totalDurationMinutes = selectedServices.reduce((total, service) => {
    console.log('🔍 Duration calculation for service:', service.service.name, {
      serviceDuration: service.service.duration_minutes,
      priceOptionDuration: service.priceOption?.duration_minutes,
      addons: selectedServiceAddons[service.service.id] || []
    });
    
    // Add service duration - use service.duration_minutes if available, otherwise fall back to priceOption
    let duration = total + (service.service.duration_minutes || service.priceOption?.duration_minutes || 0);
    
    // Add addon durations for this service
    const addons = selectedServiceAddons[service.service.id] || [];
    const addonDuration = addons.reduce((addonSum, addon) => addonSum + (addon.duration_minutes || 0), 0);
    duration += addonDuration;
    
    console.log('🔍 Service total duration:', duration, 'minutes');
    return duration;
  }, 0);

  // Add effect to reset schedule when duration changes
  useEffect(() => {
    if (scheduleData.startDate || scheduleData.startTime) {
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
    }
  }, [totalDurationMinutes]);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Add effect to set initial booking address when customer is selected
  useEffect(() => {
    if (selectedCustomer?.address) {
      console.log('Setting booking address from customer:', selectedCustomer.address);
      setBookingAddress(selectedCustomer.address);
    } else {
      console.log('No customer address available');
      setBookingAddress('');
    }
  }, [selectedCustomer]);

  // Add effect to extract postal code from booking address
  useEffect(() => {
    if (bookingAddress) {
      // Regular expression to match a Canadian postal code (e.g., V3R 1A2 or V3R1A2)
      const postalCodeRegex = /[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d/i;
      const match = bookingAddress.match(postalCodeRegex);
      
      if (match) {
        const extractedPostalCode = match[0].replace(/\s+/g, '').toUpperCase();
        console.log('Extracted postal code from address:', extractedPostalCode, 'Using FSA:', extractedPostalCode.substring(0, 3));
        
        // Only update if postal code is empty or different to avoid unnecessary state changes
        if (!postalCode || postalCode !== extractedPostalCode.substring(0, 3)) {
          // Set only the first 3 characters of the postal code (FSA)
          setPostalCode(extractedPostalCode.substring(0, 3));
        }
      } else if (bookingAddress && !postalCode) {
        console.log('No postal code detected in address:', bookingAddress);
      }
    }
  }, [bookingAddress]);

  // Load customer sources when modal opens
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      loadCustomerSources();
    }
  }, [isOpen, selectedBusiness?.id]);

  const loadCustomerSources = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const sources = await CustomerSourcesService.getCustomerSources(selectedBusiness.id);
      setCustomerSources(sources);
    } catch (error) {
      console.error('Error loading customer sources:', error);
    }
  };

  const handleCreateCustomerSource = async () => {
    if (!selectedBusiness?.id || !newSourceName.trim()) return;
    
    try {
      setCreateSourceLoading(true);
      const newSource = await CustomerSourcesService.createCustomerSource({
        business_id: selectedBusiness.id,
        name: newSourceName.trim()
      });
      
      // Add to list and select it
      setCustomerSources(prev => [...prev, newSource].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedCustomerSource(newSource);
      
      // Close modal and reset
      setIsCreateSourceModalOpen(false);
      setNewSourceName('');
    } catch (error) {
      console.error('Error creating customer source:', error);
      alert(error instanceof Error ? error.message : 'Failed to create customer source');
    } finally {
      setCreateSourceLoading(false);
    }
  };

  // Auto-detect customer source based on repeat status and booking history
  const detectCustomerSource = async (customer: Customer) => {
    if (!selectedBusiness?.id) return;
    
    try {
      // Step 1: Check customer.repeat column first (optimization)
      if (customer.repeat === true) {
        // Customer is already marked as repeat, auto-select repeat source (no modal needed)
        setSelectedCustomerSource({ 
          id: 'repeat-customer', 
          business_id: selectedBusiness.id,
          name: 'Repeat Customer', 
          is_active: true,
          created_at: '',
          updated_at: ''
        });
        console.log('✅ Auto-applied repeat customer source (already marked as repeat)');
        return;
      }
      
      // Step 2: If customer.repeat = false, count their total bookings (all statuses)
      if (!supabase) return;
      
      const { data: bookingsData, error } = await supabase
        .from('bookings')
        .select('id')
        .eq('customer_id', customer.id)
        .eq('business_id', selectedBusiness.id);
      
      if (error) {
        console.error('Error counting customer bookings:', error);
        // If error, show dropdown to be safe
        setShowCustomerSourceDropdown(true);
        return;
      }
      
      const totalBookings = bookingsData?.length || 0;
      
      // If customer has 1+ bookings, this will be their 2nd+ booking (repeat)
      if (totalBookings >= 1) {
        setSelectedCustomerSource({ 
          id: 'repeat-customer', 
          business_id: selectedBusiness.id,
          name: 'Repeat Customer', 
          is_active: true,
          created_at: '',
          updated_at: ''
        });
        
        // Update customer's repeat column to true since this will be their 2nd+ booking
        try {
          const { error: updateError } = await supabase
            .from('customers')
            .update({ 
              repeat: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', customer.id);
          
          if (updateError) {
            console.error('Error updating customer repeat status:', updateError);
          } else {
            console.log(`✅ Updated customer ${customer.first_name} ${customer.last_name} to repeat status and auto-applied repeat source`);
            // Update local customer state
            setSelectedCustomer(prev => prev ? { ...prev, repeat: true } : null);
          }
        } catch (updateError) {
          console.error('Error updating customer repeat status:', updateError);
        }
      } else {
        // Customer has 0 bookings, this will be their 1st (new customer) - show inline dropdown
        console.log('🆕 New customer detected - showing customer source dropdown');
        setSelectedCustomerSource(null);
        setShowCustomerSourceDropdown(true);
      }
      
      // Always show the dropdown for all customers, regardless of booking count
      setShowCustomerSourceDropdown(true);
      
    } catch (error) {
      console.error('Error detecting customer source:', error);
      // If error, show dropdown to be safe
      setShowCustomerSourceDropdown(true);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Note: Persistence now handled centrally by usePageLifecycle
      setTitle('Mobile Booking');
      setSelectedCustomer(null);
      setCurrentVehicle(null);
      setSelectedServices([]);
      setBookingAddress('');
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
      setError(null);
    }
  };

  const handleVehicleCreated = (vehicle: CustomerVehicle) => {
    // Set the new vehicle as current and open service selection
    setCurrentVehicle(vehicle);
    setIsCreateVehicleModalOpen(false);
    setIsSelectServiceModalOpen(true);
    
    // Save progress after vehicle creation
    saveMobileBookingProgress();
    
    // Update customer data to get the new vehicle (async)
    getCustomer(selectedCustomer?.id || '').then(updatedCustomer => {
      setSelectedCustomer(updatedCustomer);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Validation checks...
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      if (!selectedCustomer) {
        throw new Error('Please select a customer');
      }

      if (!selectedBusiness) {
        throw new Error('Please select a business');
      }

      if (selectedServices.length === 0) {
        throw new Error('Please select at least one service');
      }

      if (!selectedResource) {
        throw new Error('Please select a resource for this booking');
      }

      if (!selectedResource?.resource_id) {
        throw new Error('Invalid resource selected');
      }

      // For quick booking, use pre-filled schedule data; otherwise use selected schedule data
      const bookingStartDate = prefilledData?.isQuickBooking ? prefilledData.scheduleData?.startDate : scheduleData?.startDate;
      const bookingStartTime = prefilledData?.isQuickBooking ? prefilledData.scheduleData?.startTime : scheduleData?.startTime;
      
      if (!bookingStartDate || !bookingStartTime) {
        throw new Error('Please select a date and time for the booking');
      }
      
      // Calculate end date and time based on start time and duration
      const startDateTime = new Date(`${bookingStartDate}T${bookingStartTime}`);
      const endDateTime = new Date(startDateTime.getTime() + (totalDurationMinutes + bufferTime) * 60000);
      const bookingEndDate = endDateTime.toISOString().split('T')[0];
      const bookingEndTime = endDateTime.toTimeString().slice(0, 5);

      if (!selectedCustomerSource) {
        throw new Error('Please select a customer source');
      }
      
      // Log the buffer time being used
      console.log(`Creating mobile booking with buffer time: ${bufferTime} minutes`);

      // Calculate customer repeat status and source for tracking
      const totalBookings = await supabase
        .from('bookings')
        .select('id')
        .eq('customer_id', selectedCustomer.id)
        .eq('business_id', selectedBusiness.id);
      
      const customerRepeatStatus = selectedCustomer.repeat === true ? 'repeat' :
        ((totalBookings.data?.length || 0) >= 1 ? 'repeat' : 'new');
      const customerSourceName = selectedCustomerSource ? selectedCustomerSource.name : undefined;

      // Prepare workflow data for services that have workflows enabled
      const servicesWithWorkflowData = await Promise.all(
        selectedServices.map(async (service) => {
          const hasWorkflow = servicesWithWorkflows.has(service.service.id);
          let workflowData = null;

          if (hasWorkflow && workflowEnabled && selectedResource) {
            try {
              console.log('📋 Preparing workflow data for mobile booking service:', service.service.name);
              
              // Get the workflow template and steps
              const workflow = await getWorkflowByServiceId(service.service.id);
              if (workflow) {
                const workflowSteps = await getWorkflowSteps(workflow.id);
                
                // Fetch the resource's profile data including avatar
                const { data: resourceProfile } = await supabase
                  .from('resources')
                  .select('avatar_url, first_name, last_name')
                  .eq('id', selectedResource.resource_id)
                  .single();
                
                const resourceName = resourceProfile 
                  ? `${resourceProfile.first_name} ${resourceProfile.last_name}`.trim()
                  : resourceNames[selectedResource.id] || selectedResource.name || 'Mobile Technician';
                
                // Create workflow data with auto-assigned resource
                workflowData = workflowSteps.map((step, index) => ({
                  workflow_step_library_id: step.workflow_step_library_id || step.id,
                  title: step.title,
                  description: step.description || '',
                  order_index: step.order_index || index + 1,
                  status: 'pending',
                  completed_at: null,
                  photos_enabled: step.photos_enabled || false,
                  assigned_resources: [{
                    id: selectedResource.resource_id,
                    name: resourceName,
                    avatar_url: resourceProfile?.avatar_url || null
                  }]
                }));

                console.log(`✅ Workflow data prepared for mobile booking service ${service.service.name}:`, workflowData);
              }
            } catch (workflowError) {
              console.error('Error preparing workflow for mobile booking:', workflowError);
              // Don't fail the entire booking creation if workflow fails
            }
          }

          return { ...service, workflowData };
        })
      );

      // Create booking params object
      const bookingParams = {
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        resource_id: selectedResource.resource_id,
        title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
        start_date: bookingStartDate,
        start_time: bookingStartTime,
        end_date: bookingEndDate,
        end_time: bookingEndTime,
        buffer_time: bufferTime,
        status: 'scheduled',
        notes: `Created with ${selectedServices.length} services`,
        customer_notes: scheduleData.customer_notes || "",
        color: bookingColor,
        secondary_color: bookingSecondaryColor || undefined,
        booking_address: bookingAddress,
        customer_repeat_status: customerRepeatStatus,
        source: customerSourceName,
        created_by: currentResource?.id || 'admin',
        vehicles: Object.entries(servicesByVehicle).map(([vehicleId, services]) => ({
          vehicle_id: vehicleId,
          services: services.map(service => {
            const addons = selectedServiceAddons[service.service.id] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : 5;
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            
            // Find the corresponding service with workflow data
            const serviceWithWorkflow = servicesWithWorkflowData.find(s => s.service.id === service.service.id);
            
            return {
              service_id: service.service.id,
              name: service.service.name,
              description: service.service.description || '',
              price: servicePrice,
              tax_exempt: service.service.tax_exempt || false,
              tax_rate: taxRate,
              tax_amount: taxAmount,
              total_amount: taxableAmount + taxAmount,
              duration_minutes: service.priceOption.duration_minutes,
              workflow_steps: serviceWithWorkflow?.workflowData || null
            };
          })
        }))
      };
      
      console.log('Creating mobile booking with params:', {
        ...bookingParams,
        buffer_time: bookingParams.buffer_time // Explicitly log the buffer time
      });

      // Handle rescheduling - cancel automations, soft delete booking, hard delete mobile_bookings
      if (isReschedule && existingBookingId) {
        console.log('[MOBILE RESCHEDULE] 🔄 Starting reschedule process for booking:', existingBookingId);
        
        try {
          // 🚫 Step 1: Cancel existing automations FIRST
          if (selectedBusiness?.id) {
            try {
              console.log('[MOBILE RESCHEDULE] 🚫 Cancelling existing automations for booking:', existingBookingId);
              const cancellationResult = await cancelBookingAutomations(
                existingBookingId,
                'mobile_booking_rescheduled',
                selectedBusiness.id
              );
              
              if (cancellationResult.success) {
                console.log(`[MOBILE RESCHEDULE] ✅ Successfully cancelled ${cancellationResult.cancelledJobs} automation jobs`);
              } else {
                console.warn(`[MOBILE RESCHEDULE] ⚠️ Automation cancellation had issues:`, cancellationResult.errors);
              }
            } catch (automationError) {
              console.error('[MOBILE RESCHEDULE] ❌ Error cancelling automations:', automationError);
              // Don't fail the reschedule if automation cancellation fails
            }
          }
          
          // 🗑️ Step 2: Soft delete the booking (preserve for automation system)
          console.log('[MOBILE RESCHEDULE] 🗑️ Soft deleting booking entry:', existingBookingId);
          const { error: softDeleteError } = await supabase
            .from('bookings')
            .update({ 
              is_deleted: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingBookingId);
            
          if (softDeleteError) {
            console.error('Error soft deleting existing booking:', softDeleteError);
            throw new Error('Failed to soft delete existing booking');
          }
          
          // 💥 Step 3: Hard delete mobile_bookings entries (for resource scheduling)
          console.log('[MOBILE RESCHEDULE] 💥 Hard deleting mobile_bookings entries for booking:', existingBookingId);
          const { error: hardDeleteError } = await supabase
            .from('mobile_bookings')
            .delete()
            .eq('booking_id', existingBookingId);
            
          if (hardDeleteError) {
            console.error('Error hard deleting mobile_bookings entries:', hardDeleteError);
            throw new Error('Failed to hard delete mobile_bookings entries');
          }
          
          console.log('[MOBILE RESCHEDULE] ✅ Successfully completed reschedule cleanup (automations cancelled, booking soft deleted, mobile_bookings hard deleted)');
        } catch (deleteError) {
          console.error('Error during mobile reschedule cleanup:', deleteError);
          throw new Error('Failed to cleanup existing booking for mobile rescheduling');
        }
      }

      // Create the booking
      const result = await createMobileBooking(bookingParams);

      if (!result || !result.id) throw new Error('No booking data returned');

      // Insert add-ons for each service (workflow data is now handled during booking creation)
      for (const serviceData of selectedServices) {
        const addons = selectedServiceAddons[serviceData.service.id] || [];
        
        if (addons.length > 0) {
          const { data: serviceResult } = await supabase
            .from('booking_services')
            .select('id')
            .eq('booking_id', result.id)
            .eq('service_id', serviceData.service.id)
            .single();

          if (serviceResult) {
            // Insert add-ons
            const { error: addonsError } = await supabase
              .from('booking_service_addons')
              .insert(
                addons.map(addon => ({
                  booking_service_id: serviceResult.id,
                  addon_id: addon.id,
                  price: addon.price
                }))
              );

            if (addonsError) throw addonsError;
          }
        }
      }

      // Reset all state before closing
      setTitle('Mobile Booking');
      setSelectedCustomer(null);
      setCurrentVehicle(null);
      setSelectedServices([]);
      setSelectedServiceAddons({});
      setBookingAddress('');
      setPostalCode('');
      setSelectedResource(null);
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
      setError(null);
      
      // Reset workflow state
      setServicesWithWorkflows(new Set());
      setWorkflowEnabled(true);

      // Clear saved modal state
              // Removed persistence - no longer needed

      // Trigger automations for mobile booking creation
      try {
        console.log('🤖 MOBILE BOOKING: Triggering booking automations for created event...');
        
        // Build comprehensive booking data for automation (same structure as CreateBookingModal)
        const realBookingData = {
          booking_id: result.booking_number, // ✅ Use booking_number (number) for automation system lookup
          booking_number: result.booking_number,
          title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
          grand_total: selectedServices.reduce((total, service) => {
            const addons = selectedServiceAddons[service.service.id] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : (service.service.tax_rate || 5);
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            return total + taxableAmount + taxAmount;
          }, 0),
          
          // Customer information
          customer: {
            id: selectedCustomer.id,
            name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            full_name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            first_name: selectedCustomer.first_name,
            last_name: selectedCustomer.last_name,
            email: selectedCustomer.email,
            phone: selectedCustomer.phone
          },
          
          // Schedule information
          schedule: {
            start_date: prefilledData?.isQuickBooking ? prefilledData.scheduleData?.startDate : scheduleData.startDate,
            start_time: prefilledData?.isQuickBooking ? prefilledData.scheduleData?.startTime : scheduleData.startTime,
            end_date: prefilledData?.isQuickBooking ? prefilledData.scheduleData?.endDate : scheduleData.endDate,
            end_time: prefilledData?.isQuickBooking ? prefilledData.scheduleData?.endTime : scheduleData.endTime,
            drop_off_date: prefilledData?.isQuickBooking ? prefilledData.scheduleData?.dropOffDate : scheduleData.dropOffDate,
            drop_off_time: prefilledData?.isQuickBooking ? prefilledData.scheduleData?.dropOffTime : scheduleData.dropOffTime
          },
          
          // Services information
          services: selectedServices.map(service => ({
            id: service.service.id,
            name: service.service.name,
            price: service.priceOption.price,
            duration_minutes: service.priceOption.duration_minutes,
            vehicle: {
              id: service.vehicle.id,
              make: service.vehicle.make,
              model: service.vehicle.model,
              year: service.vehicle.year,
              color: service.vehicle.color
            },
            addons: selectedServiceAddons[service.service.id] || []
          })),
          
          // Resource assigned (mobile booking has a single resource)
          resources: selectedResource ? [{
            id: selectedResource.resource_id,
            name: resourceNames[selectedResource.resource_id] || 'Mobile Technician',
            phone: '', // Mobile resource phone not available in this context
            role: 'mobile_technician'
          }] : [],
          
          // Vehicle summary for easy access
          vehicles: (() => {
            const vehicles = selectedServices.map(service => ({
              make: service.vehicle.make || '',
              model: service.vehicle.model || '',
              year: service.vehicle.year || 0,
              color: service.vehicle.color || '',
              display: `${service.vehicle.year || ''} ${service.vehicle.make || ''} ${service.vehicle.model || ''}`.trim()
            }));
            
            // Remove duplicates based on make/model/year combination
            const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
              index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
            );
            
            return uniqueVehicles;
          })(),
          
          // Additional booking details
          notes: scheduleData.customer_notes || null,
          booking_address: bookingAddress || null,
          color: bookingColor,
          secondary_color: bookingSecondaryColor || null,
          created_at: new Date().toISOString(),
          status: 'scheduled',
          mobile_booking: true,
          source: isReschedule ? 'mobile_reschedule_action' : 'mobile_booking_creation'
        };

        console.log('📋 Mobile booking data:', realBookingData);
        
        // Trigger automation (non-blocking)
        const eventType = isReschedule ? 'created' : 'created'; // Both use 'created' but source differs
        const logPrefix = isReschedule ? 'MOBILE RESCHEDULE' : 'MOBILE BOOKING';
        
        triggerBookingAutomations(selectedBusiness.id, eventType, realBookingData)
          .then(() => console.log(`🤖 ${logPrefix}: Booking automations completed successfully`))
          .catch(automationError => console.error(`❌ ${logPrefix}: Error triggering booking automations:`, automationError));
          
        console.log(`🤖 ${logPrefix}: Booking automation trigger initiated`);
      } catch (automationError) {
        console.error('❌ MOBILE BOOKING: Failed to trigger booking automations:', automationError);
        // We don't throw here to avoid blocking the booking creation flow
      }

      // Note: Persistence now handled centrally by usePageLifecycle
              console.log('🗑️ Mobile booking created successfully');

      // Call success callback and close modal
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error creating booking:', error);
      setError(error instanceof Error ? error.message : 'Failed to create booking. Please check all required fields.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async (service: ExtendedService, priceOption: any) => {
    if (!currentVehicle) return;

    const newService = {
      vehicle: currentVehicle,
      service,
      priceOption
    };

    const newSelectedServices = [...selectedServices, newService];
    console.log('📱 Service added in CreateMobileBookingModal:', service.name);
    setSelectedServices(newSelectedServices);
    
    // Check if this service has a workflow template
    await checkServiceForWorkflow(service.id);
    
    // Save progress immediately with the new services array
    saveMobileBookingProgress({ selectedServices: newSelectedServices });
    
    // After adding the service, show the add-ons modal
    setCurrentService(service);
    setIsSelectAddonsModalOpen(true);
  };

  // Function to check if a service has a workflow template
  const checkServiceForWorkflow = async (serviceId: string) => {
    try {
      const workflow = await getWorkflowByServiceId(serviceId);
      if (workflow) {
        console.log('📋 Found workflow template for service:', serviceId, workflow.name);
        setServicesWithWorkflows(prev => new Set(prev.add(serviceId)));
      }
    } catch (error) {
      console.log('📋 No workflow template found for service:', serviceId);
    }
  };

  const handleAddAddons = (addons: ServiceAddon[]) => {
    if (!currentService) return;
    
    const newAddons = {
      ...selectedServiceAddons,
      [currentService.id]: addons
    };
    
    console.log('📱 Add-ons added in CreateMobileBookingModal for service:', currentService.name);
    setSelectedServiceAddons(newAddons);
    
    // Save progress immediately with the new addons
    saveMobileBookingProgress({ selectedServiceAddons: newAddons });
  };

  // Group services by vehicle
  const servicesByVehicle = selectedServices.reduce<Record<string, typeof selectedServices>>((acc, service) => {
    const vehicleId = service.vehicle.id;
    if (!acc[vehicleId]) {
      acc[vehicleId] = [];
    }
    acc[vehicleId].push(service);
    return acc;
  }, {});

  const handleSelectVehicle = (vehicle: CustomerVehicle) => {
    // If we already have services for this vehicle, keep using it
    const existingVehicleServices = selectedServices.find(s => s.vehicle.id === vehicle.id);
    const vehicleToSet = existingVehicleServices ? existingVehicleServices.vehicle : vehicle;
    
    console.log('📱 Vehicle selected in CreateMobileBookingModal:', vehicleToSet);
    setCurrentVehicle(vehicleToSet);
    setIsSelectServiceModalOpen(true);
    setIsSelectVehicleModalOpen(false);
    
    // Save progress immediately with the new vehicle data
    saveMobileBookingProgress({ currentVehicle: vehicleToSet });
  };

  const handleRemoveService = (vehicleId: string, serviceId: string) => {
    setSelectedServices(prev => prev.filter(s => !(s.vehicle.id === vehicleId && s.service.id === serviceId)));
    
    // Save progress after removing service
    setTimeout(() => saveMobileBookingProgress(), 100);
  };

  const handleRemoveAddon = (serviceId: string, addonId: string) => {
    setSelectedServiceAddons(prev => ({
      ...prev,
      [serviceId]: prev[serviceId]?.filter(addon => addon.id !== addonId) || []
    }));
    
    // Save progress after removing addon
    setTimeout(() => saveMobileBookingProgress(), 100);
  };

  // Fetch mobile availability data
  const fetchMobileAvailability = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      setAvailabilityLoading(true);
      setAvailabilityError(null);

      const { data, error } = await supabase
        .from('mobile_availability')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('mobile_available', true);

      if (error) throw error;

      console.log('Fetched mobile availability:', data);
      setMobileAvailability(data || []);
    } catch (err) {
      console.error('Error fetching mobile availability:', err);
      setAvailabilityError(err instanceof Error ? err.message : 'Failed to fetch mobile availability');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  // Fetch buffer time from online_booking_settings
  const fetchBufferTime = async () => {
    if (!supabase || !selectedBusiness?.id) return;
    
    try {
      setBufferTimeLoading(true);
      
      const { data, error } = await supabase
        .from('online_booking_settings')
        .select('buffer_time')
        .eq('business_id', selectedBusiness.id)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          // Record not found, keeping default value
          console.log('No buffer time setting found, using default');
        } else {
          console.error('Error fetching buffer time:', error);
        }
        return;
      }
      
      if (data?.buffer_time !== undefined && data?.buffer_time !== null) {
        console.log(`Setting buffer time to ${data.buffer_time} minutes from database`);
        setBufferTime(data.buffer_time);
      }
    } catch (error) {
      console.error('Error fetching buffer time:', error);
    } finally {
      setBufferTimeLoading(false);
    }
  };

  // Fetch availability data when modal opens
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      fetchMobileAvailability();
      fetchBufferTime();
    }
  }, [isOpen, selectedBusiness?.id]);

  // Function to fetch blocked time slots
  const fetchBlockedTimeSlots = async () => {
    if (!supabase) {
      console.error('Supabase client is not initialized');
      setBlockedSlotsError('Database connection not available');
      setBlockedSlotsLoading(false);
      return;
    }

    try {
      setBlockedSlotsLoading(true);
      setBlockedSlotsError(null);

      // Get current date at start of day in local timezone
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const localDateStr = format(now, 'yyyy-MM-dd');

      // Fetch all future mobile bookings for this business, including buffer blocks
      const { data: mobileBookings, error } = await supabase
        .from('mobile_bookings')
        .select('*')
        .eq('business_id', selectedBusiness?.id)
        .gte('start_date', localDateStr)
        .order('start_date', { ascending: true });

      if (error) throw error;

      console.log('Fetched mobile bookings for blocked slots:', mobileBookings);

      // Process the bookings into blocked time slots
      const blocked: BlockedTimeSlots = {};

      mobileBookings.forEach(booking => {
        const { resource_id, start_date, start_time, end_time, is_buffer_block } = booking;

        // Log buffer blocks for debugging
        if (is_buffer_block) {
          console.log('Processing buffer block:', {
            resource_id,
            start_date,
            start_time,
            end_time
          });
        }

        // Initialize array for this resource if it doesn't exist
        if (!blocked[resource_id]) {
          blocked[resource_id] = [];
        }

        // Find or create the entry for this date
        let dateEntry = blocked[resource_id].find(entry => entry.date === start_date);
        if (!dateEntry) {
          dateEntry = { date: start_date, blockedSlots: [] };
          blocked[resource_id].push(dateEntry);
        }

        // Calculate all 30-minute slots between start_time and end_time
        const [startHour, startMinute] = start_time.split(':').map(Number);
        const [endHour, endMinute] = end_time.split(':').map(Number);
        const startMinutes = startHour * 60 + startMinute;
        const endMinutes = endHour * 60 + endMinute;

        // Generate all 30-minute slots that are blocked
        for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
          const hour = Math.floor(minutes / 60);
          const minute = minutes % 60;
          const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          if (!dateEntry.blockedSlots.includes(timeSlot)) {
            dateEntry.blockedSlots.push(timeSlot);
          }
        }
      });

      console.log('Blocked time slots:', blocked);
      setBlockedTimeSlots(blocked);
    } catch (error) {
      console.error('Error fetching blocked time slots:', error);
      setBlockedSlotsError(error instanceof Error ? error.message : 'Error fetching blocked time slots');
    } finally {
      setBlockedSlotsLoading(false);
    }
  };

  // Fetch blocked time slots when modal opens or business changes
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      fetchBlockedTimeSlots();
    }
  }, [isOpen, selectedBusiness?.id]);

  // Update the isTimeSlotAvailable function to be more efficient
  const isTimeSlotAvailable = (date: Date, timeString: string) => {
    // If we're still loading blocked slots, return false
    if (blockedSlotsLoading) return false;

    // Get available resources for this time slot
    const availableResources = getAvailableResourcesForTimeSlot(date, timeString);
    
    // If there are no available resources, the time slot is not available
    return availableResources.length > 0;
  };

  // Update the isDayAvailable function with better debugging for reschedule mode
  const isDayAvailable = (date: Date) => {
    const localDate = new Date(date);
    const offset = localDate.getTimezoneOffset();
    localDate.setMinutes(localDate.getMinutes() + offset);
    const dayOfWeek = format(localDate, 'EEEE').toLowerCase();
    
    // Debug logging for reschedule mode
    if (isReschedule) {
      console.log('🔍 Checking day availability for reschedule:', {
        date: format(date, 'yyyy-MM-dd'),
        dayOfWeek,
        postalCode,
        mobileAvailabilityCount: mobileAvailability.length
      });
    }
    
    // For reschedule mode, first try normal postal code checking
    let hasAvailableResource = mobileAvailability.some(resource => {
      try {
        // Only check postal code availability if a postal code is provided
        if (postalCode && postalCode.trim() !== '') {
          let postalCodes = resource.postal_codes;
          if (typeof postalCodes === 'string') {
            try {
              postalCodes = JSON.parse(postalCodes);
            } catch (err) {
              if (isReschedule) console.log('❌ Failed to parse postal codes for resource:', resource.id);
              return false;
            }
          }
          
          const availablePostalCodes = postalCodes?.[dayOfWeek] || [];
          const enteredPostalCode = postalCode.toUpperCase().replace(/\s+/g, '');
          
          if (isReschedule) {
            console.log('📮 Postal code check:', {
              resourceId: resource.id,
              dayOfWeek,
              enteredPostalCode,
              availablePostalCodes,
              availablePostalCodesCount: availablePostalCodes.length,
              isMatch: availablePostalCodes.includes(enteredPostalCode),
              // Show first few postal codes for debugging
              samplePostalCodes: availablePostalCodes.slice(0, 5)
            });
          }
          
          if (!availablePostalCodes.includes(enteredPostalCode)) {
            return false;
          }
        }
        
        // Check working hours
        let workingHours = resource.working_hours;
        if (typeof workingHours === 'string') {
          try {
            workingHours = JSON.parse(workingHours);
          } catch (err) {
            if (isReschedule) console.log('❌ Failed to parse working hours for resource:', resource.id);
            return false;
          }
        }
        
        const daySchedule = workingHours?.[dayOfWeek];
        const hasWorkingHours = daySchedule?.enabled && daySchedule?.shifts?.length > 0;
        
        if (isReschedule) {
          console.log('⏰ Working hours check:', {
            resourceId: resource.id,
            dayOfWeek,
            hasSchedule: !!daySchedule,
            enabled: daySchedule?.enabled,
            shiftsCount: daySchedule?.shifts?.length || 0,
            hasWorkingHours
          });
        }

        return hasWorkingHours;
      } catch (err) {
        console.error('Error checking day availability:', err);
        return false;
      }
    });
    
    // Fallback for reschedule mode: if no resources match the postal code,
    // check if any resources have working hours (ignore postal code restriction)
    if (!hasAvailableResource && isReschedule && postalCode && postalCode.trim() !== '') {
      console.log('🔄 Reschedule fallback: Checking availability without postal code restriction');
      
      hasAvailableResource = mobileAvailability.some(resource => {
        try {
          // Check working hours only (skip postal code check)
          let workingHours = resource.working_hours;
          if (typeof workingHours === 'string') {
            try {
              workingHours = JSON.parse(workingHours);
            } catch (err) {
              return false;
            }
          }
          
          const daySchedule = workingHours?.[dayOfWeek];
          const hasWorkingHours = daySchedule?.enabled && daySchedule?.shifts?.length > 0;
          
          if (hasWorkingHours && isReschedule) {
            console.log('✅ Reschedule fallback: Found resource with working hours:', {
              resourceId: resource.id,
              dayOfWeek,
              shiftsCount: daySchedule?.shifts?.length || 0
            });
          }

          return hasWorkingHours;
        } catch (err) {
          console.error('Error in reschedule fallback check:', err);
          return false;
        }
      });
      
      if (hasAvailableResource) {
        console.log('✅ Reschedule fallback successful: Using working hours only');
      }
    }
    
    if (isReschedule) {
      console.log('✅ Final day availability result:', {
        date: format(date, 'yyyy-MM-dd'),
        hasAvailableResource,
        usedFallback: !hasAvailableResource && isReschedule
      });
    }
    
    return hasAvailableResource;
  };

  // Update the hasTimePassed function to remove verbose logging
  const hasTimePassed = (date: Date | string, timeString: string): boolean => {
    const now = new Date();
    const [hours, minutes] = timeString.split(':').map(Number);
    const checkDate = typeof date === 'string' ? new Date(date + 'T12:00:00Z') : new Date(date);

    // Compare dates in UTC to avoid timezone issues
    const nowUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const checkUTC = Date.UTC(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());

    // For future dates, only check if the time is within business hours (9am-5pm)
    if (checkUTC > nowUTC) {
      const slotMinutes = hours * 60 + minutes;
      return slotMinutes < (9 * 60) || slotMinutes > (17 * 60);
    }

    // For today, check if the time has passed
    if (checkUTC === nowUTC) {
      const slotMinutes = hours * 60 + minutes;
      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
      return currentTimeMinutes >= slotMinutes;
    }

    // Past dates are always considered passed
    return true;
  };

  // Update the handleDateSelect function with better logging
  const handleDateSelect = (day: number) => {
    try {
      // Create date at noon UTC to avoid any timezone issues
      const selectedDate = new Date(Date.UTC(
        currentMonth.getUTCFullYear(),
        currentMonth.getUTCMonth(),
        day,
        12, // Set to noon UTC
        0,
        0
      ));
      
      // Format date as YYYY-MM-DD using UTC date
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      
      console.log('Setting new date:', {
        selectedDate: selectedDate.toISOString(),
        formattedDate,
        day,
        currentMonth: currentMonth.toISOString()
      });

      // Reset all time-related fields when selecting a new date
      setScheduleData(prev => ({
        ...prev,
        dropOffDate: formattedDate,
        startDate: formattedDate,
        endDate: formattedDate,
        // Reset time fields
        dropOffTime: '',
        startTime: '',
        endTime: ''
      }));

      // Also reset the selected resource since it's tied to a specific time slot
      setSelectedResource(null);
    } catch (err) {
      console.error('Error selecting date:', err);
      setError('Failed to select date. Please try again.');
    }
  };

  // Update the renderCalendar function to check postal code for day availability
  const renderCalendar = () => {
    const days = getDaysInMonthArray(currentMonth);
    const dayHeaders = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const today = new Date();

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700 flex items-center">
            <Clock className="h-4 w-4 mr-2 text-blue-600" />
            Schedule
          </h3>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="p-2 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
            >
              <ChevronDown className="w-4 h-4 transform rotate-90 text-gray-600" />
            </button>
            <span className="text-sm font-medium text-gray-700 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="p-2 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
            >
              <ChevronDown className="w-4 h-4 transform -rotate-90 text-gray-600" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-3">
          {dayHeaders.map((day) => (
            <div key={day} className="text-center text-sm font-medium text-blue-600 py-2">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-2">
          {days.map((day, index) => {
            if (!day) return <div key={index} className="aspect-square" />;
            
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const dateStr = format(date, 'yyyy-MM-dd');
            const isSelected = dateStr === scheduleData.startDate;
            const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const hasAvailability = isDayAvailable(date);

            return (
              <button
                key={index}
                type="button"
                disabled={isPast || !hasAvailability}
                onClick={() => !isPast && hasAvailability && handleDateSelect(day)}
                className={`
                  aspect-square rounded-lg text-center relative transition-colors font-medium text-sm border
                  ${isPast || !hasAvailability 
                    ? 'cursor-not-allowed opacity-50 bg-gray-100 border-gray-200 text-gray-400' 
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300'
                  }
                  ${isSelected 
                    ? 'bg-blue-600 text-white border-blue-600' 
                    : ''
                  }
                  ${date.getDate() === today.getDate() && 
                    date.getMonth() === today.getMonth() && 
                    date.getFullYear() === today.getFullYear() 
                    ? 'font-bold ring-2 ring-blue-200' 
                    : ''}
                `}
                style={{ 
                  touchAction: 'manipulation',
                  minHeight: '44px'
                }}
              >
                {day}
                {hasAvailability && !isPast && !isSelected && (
                  <div 
                    className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full bg-blue-500"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Time slots */}
        {scheduleData.startDate && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <Clock className="h-4 w-4 mr-2 text-blue-600" />
              Available Times
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {generateTimeSlots().map((time) => {
                const date = new Date(scheduleData.startDate);
                const availableResources = getAvailableResourcesForTimeSlot(date, time);
                const isAvailable = availableResources.length > 0;
                const isSelected = scheduleData.startTime === time;
                
                return (
                  <button
                    key={time}
                    type="button"
                    disabled={!isAvailable}
                    onClick={() => {
                      if (isAvailable) {
                        handleTimeSlotSelect(time);
                      }
                    }}
                    className={`
                      px-3 py-2 text-sm font-medium rounded-lg text-center transition-colors border
                      ${isSelected 
                        ? 'bg-blue-600 text-white border-blue-600' 
                        : isAvailable 
                          ? 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-300' 
                          : 'bg-gray-100 text-gray-400 border-gray-200'
                      }
                      ${!isAvailable ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                    style={{ 
                      touchAction: 'manipulation',
                      minHeight: '44px'
                    }}
                  >
                    <div className="font-bold">
                      {format(new Date(`2000-01-01T${time}`), 'h:mm a')}
                    </div>
                    {isAvailable && availableResources.length > 1 && (
                      <div className={`text-xs mt-0.5 ${isSelected ? 'text-purple-200' : 'text-purple-600'}`}>
                        {availableResources.length} available
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
                  </div>
      )}

      {/* Customer Source Selection Modal */}
      {isCustomerSourceSelectionModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100004]">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gray-800 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Select Customer Source</h3>
                    <p className="text-sm text-gray-300">How did this customer find you?</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsCustomerSourceSelectionModalOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {/* Existing Customer Sources */}
                {customerSources.map((source) => (
                  <button
                    key={source.id}
                    onClick={() => {
                      setSelectedCustomerSource(source);
                      setIsCustomerSourceSelectionModalOpen(false);
                    }}
                    className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all duration-200"
                  >
                    <span className="font-medium text-gray-900">{source.name}</span>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </button>
                ))}

                {/* Add New Source Option */}
                <button
                  onClick={() => {
                    setIsCustomerSourceSelectionModalOpen(false);
                    setIsCreateSourceModalOpen(true);
                  }}
                  className="w-full flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all duration-200"
                >
                  <Plus className="h-4 w-4 text-purple-600 mr-2" />
                  <span className="font-medium text-purple-600">Add New Source</span>
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setIsCustomerSourceSelectionModalOpen(false)}
                className="w-full px-4 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-100 transition-colors"
              >
                Skip for Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

  // Update the fetchResourceNames function to directly query resources table
  const fetchResourceNames = async (resourceIds: string[]) => {
    if (!supabase || !resourceIds.length) return;

    try {
      // Get the resource_ids from mobile_availability records
      const resourceIdsToFetch = mobileAvailability
        .filter(ma => resourceIds.includes(ma.id))
        .map(ma => ma.resource_id);

      console.log('Fetching names for resource_ids:', resourceIdsToFetch);
      
      const { data, error } = await supabase
        .from('resources')
        .select('id, first_name, last_name')
        .in('id', resourceIdsToFetch);

      if (error) throw error;

      console.log('Fetched resource data:', data);
      
      // Map the resource names back to mobile_availability ids
      const names = mobileAvailability.reduce((acc, ma) => {
        const resource = data?.find(r => r.id === ma.resource_id);
        if (resource) {
          console.log(`Found name for resource ${ma.resource_id}:`, `${resource.first_name} ${resource.last_name}`);
          return {
            ...acc,
            [ma.id]: `${resource.first_name} ${resource.last_name}`
          };
        }
        return {
          ...acc,
          [ma.id]: `Resource ${ma.resource_id}`
        };
      }, {} as Record<string, string>);

      console.log('Setting resource names:', names);
      setResourceNames(names);
    } catch (err) {
      console.error('Error fetching resource names:', err);
    }
  };

  // Update useEffect to fetch resource names when mobileAvailability changes
  useEffect(() => {
    if (mobileAvailability.length > 0) {
      const resourceIds = mobileAvailability.map(r => r.id);
      fetchResourceNames(resourceIds);
    }
  }, [mobileAvailability]);

  // Update the handleTimeSlotSelect function to separate buffer time from displayed end time
  const handleTimeSlotSelect = (time: string) => {
    console.log('Time slot selected:', {
      time,
      currentScheduleData: scheduleData,
      hasStartDate: !!scheduleData.startDate,
      serviceDuration: totalDurationMinutes,
      bufferTime,
      totalWithBuffer: totalDurationMinutes + bufferTime
    });

    if (!scheduleData.startDate) {
      console.error('No start date set when selecting time slot');
      return;
    }

    const date = new Date(scheduleData.startDate);
    const resources = getAvailableResourcesForTimeSlot(date, time);
    
    console.log('Available resources for time slot:', {
      time,
      date: date.toISOString(),
      resourceCount: resources.length,
      resources: resources.map(r => r.id)
    });

    if (resources.length === 0) {
      console.error('No available resources for selected time slot');
      return;
    }

    // Create start date/time
    const [hours, minutes] = time.split(':').map(Number);
    const startDateTime = new Date(scheduleData.startDate + 'T' + time);
    
    // Calculate end date/time by adding only the service duration (not buffer time)
    // Buffer time is used for availability but is not shown to the customer
    const endDateTime = new Date(startDateTime.getTime() + totalDurationMinutes * 60000);
    
    // Format end date and time separately
    const endDate = format(endDateTime, 'yyyy-MM-dd');
    const endTime = format(endDateTime, 'HH:mm');

    console.log('Setting schedule data:', {
      startDateTime: startDateTime.toISOString(),
      endDateTime: endDateTime.toISOString(),
      startDate: scheduleData.startDate,
      startTime: time,
      endDate,
      endTime,
      serviceDuration: totalDurationMinutes,
      bufferTime,
      totalWithBuffer: totalDurationMinutes + bufferTime
    });

    if (resources.length === 1) {
      // Auto-select the only available resource
      console.log('📱 Auto-selecting single available resource in CreateMobileBookingModal:', {
        resource: resources[0],
        resourceId: resources[0].resource_id,
        resourceName: resourceNames[resources[0].id]
      });
      
      const newScheduleData = {
        ...scheduleData,
        dropOffTime: time,
        startTime: time,
        endDate: endDate,
        endTime: endTime
      };
      
      setSelectedResource(resources[0]);
      setScheduleData(newScheduleData);
      
      // Save progress immediately with both schedule and resource data
      saveMobileBookingProgress({ 
        scheduleData: newScheduleData,
        selectedResource: resources[0]
      });
    } else {
      // Show resource selector for multiple available resources
      console.log('📱 Multiple resources available in CreateMobileBookingModal:', {
        count: resources.length,
        resources: resources.map(r => ({
          id: r.id,
          resourceId: r.resource_id,
          name: resourceNames[r.id]
        }))
      });
      
      const newScheduleData = {
        ...scheduleData,
        dropOffTime: time,
        startTime: time,
        endDate: endDate,
        endTime: endTime
      };
      
      setAvailableResources(resources);
      setIsResourceSelectorOpen(true);
      setScheduleData(newScheduleData);
      
      // Save progress immediately with schedule data
      saveMobileBookingProgress({ scheduleData: newScheduleData });
    }
  };

  // Add ResourceSelector modal component
  const ResourceSelector = () => {
    if (!isResourceSelectorOpen) return null;

    const hasUnloadedNames = availableResources.some(r => !resourceNames[r.id]);

    const handleResourceSelect = (resource: Resource) => {
      console.log('📱 Resource selected in CreateMobileBookingModal:', {
        resource,
        resourceId: resource.resource_id,
        resourceName: resourceNames[resource.id]
      });
      setSelectedResource(resource);
      setIsResourceSelectorOpen(false);
      
      // Save progress immediately with the new resource
      saveMobileBookingProgress({ selectedResource: resource });
    };

    return (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100005]">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Select Resource</h3>
          <div className="space-y-2">
            {hasUnloadedNames ? (
              <div className="text-sm text-gray-600">Loading resources...</div>
            ) : (
              availableResources.map(resource => (
                <button
                  key={resource.id}
                  onClick={() => handleResourceSelect(resource)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  {resourceNames[resource.id] || resource.name}
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => setIsResourceSelectorOpen(false)}
            className="mt-4 w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Update the payment summary section to show end time with buffer
  const renderPaymentSummary = () => (
    <div>
      <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center">
        <DollarSign className="h-4 w-4 mr-2 text-blue-600" />
        Payment Summary
      </h3>
      
      {scheduleData?.startDate && scheduleData?.startTime && (
        <div className="mb-4 space-y-2 pb-4 border-b border-gray-200">
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-2">
            <div className="text-sm font-medium text-gray-700">
              Date: <span className="text-blue-700 font-semibold">{format(new Date(scheduleData.startDate + 'T12:00:00Z'), 'EEEE, MMMM d, yyyy')}</span>
            </div>
            <div className="text-sm font-medium text-gray-700">
              Start Time: <span className="text-blue-700 font-semibold">{format(new Date(`2000-01-01T${scheduleData.startTime}`), 'h:mm a')}</span>
            </div>
            <div className="text-sm font-medium text-gray-700">
              End Time: <span className="text-blue-700 font-semibold">{format(new Date(`2000-01-01T${scheduleData.startTime}`).getTime() + totalDurationMinutes * 60000, 'h:mm a')}</span>
            </div>
            <div className="text-sm font-medium text-blue-600 bg-blue-50 rounded-lg p-2 border border-blue-200">
              End Time with Buffer: <span className="font-semibold">{format(new Date(`2000-01-01T${scheduleData.startTime}`).getTime() + (totalDurationMinutes + bufferTime) * 60000, 'h:mm a')}</span>
            </div>
            {selectedResource && (
              <div className="text-sm font-medium text-gray-700">
                Resource: <span className="text-blue-700 font-semibold">{resourceNames[selectedResource.id] || selectedResource.name}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {!prefilledData?.isQuickBooking && (
        <div className="space-y-3 mb-4 pb-4 border-b border-gray-200">
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="h-4 w-4 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-gray-700 flex-1">Service Duration</span>
            <span className="text-sm font-semibold text-blue-700">
              {totalDurationMinutes >= 1440 
                ? `${Math.floor(totalDurationMinutes / 1440)} days` 
                : totalDurationMinutes >= 60
                  ? `${Math.floor(totalDurationMinutes / 60)} hours ${totalDurationMinutes % 60 > 0 ? `${totalDurationMinutes % 60} mins` : ''}`
                  : `${totalDurationMinutes} mins`}
            </span>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="h-4 w-4 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-blue-700 flex-1">Total Duration with Buffer Time</span>
            <span className="text-sm font-semibold text-blue-800">
              {(totalDurationMinutes + bufferTime) >= 1440 
                ? `${Math.floor((totalDurationMinutes + bufferTime) / 1440)} days` 
                : (totalDurationMinutes + bufferTime) >= 60
                  ? `${Math.floor((totalDurationMinutes + bufferTime) / 60)} hours ${(totalDurationMinutes + bufferTime) % 60 > 0 ? `${(totalDurationMinutes + bufferTime) % 60} mins` : ''}`
                  : `${totalDurationMinutes + bufferTime} mins`}
            </span>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {/* Service Breakdown */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-2">
          {selectedServices.map((item) => {
            const addons = selectedServiceAddons[item.service.id] || [];
            return (
              <div key={`${item.service.id}-${item.vehicle.id}`} className="text-sm text-gray-600 flex items-center">
                <div className="h-2 w-2 bg-blue-500 rounded-full mr-2"></div>
                <span className="font-medium">{item.service.name} {addons.length > 0 ? `(+ ${addons.length} add-ons)` : ''}</span>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-lg p-3 border border-gray-200 space-y-2">
          <div className="flex justify-between text-sm text-gray-700">
            <span className="font-medium">Subtotal</span>
            <span className="font-semibold text-gray-900">${(selectedServices.reduce((sum, s) => {
              let total = sum + s.priceOption.price;
              const addons = selectedServiceAddons[s.service.id] || [];
              total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
              return total;
            }, 0)).toLocaleString()}</span>
          </div>

          <div className="flex justify-between text-sm text-gray-700">
            <span className="font-medium">GST (5%)</span>
            <span className="font-semibold text-gray-900">+ ${(selectedServices.reduce((sum, s) => {
              let total = s.priceOption.price;
              const addons = selectedServiceAddons[s.service.id] || [];
              total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
              return s.service.tax_exempt ? sum : sum + (total * 0.05);
            }, 0)).toLocaleString()}</span>
          </div>
        </div>

        <div className="bg-blue-600 rounded-lg p-4 border border-blue-600">
          <div className="flex justify-between text-base font-semibold text-white">
            <span>Total</span>
            <span>${(selectedServices.reduce((sum, s) => {
              let total = s.priceOption.price;
              const addons = selectedServiceAddons[s.service.id] || [];
              total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
              if (!s.service.tax_exempt) {
                total += total * 0.05;
              }
              return sum + total;
            }, 0)).toLocaleString()}</span>
          </div>
        </div>

        {/* Workflow Checkbox - Only show if any selected services have workflows */}
        {selectedServices.some(service => servicesWithWorkflows.has(service.service.id)) && (
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 mt-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={workflowEnabled}
                onChange={(e) => setWorkflowEnabled(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-white border-blue-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <CheckCircle className="h-3 w-3 text-white" />
                </div>
                <div>
                  <span className="text-xs sm:text-sm font-medium text-blue-800">Include Workflow</span>
                  <p className="text-xs text-blue-600 mt-0.5">
                    Auto-assign {selectedResource ? resourceNames[selectedResource.id] || selectedResource.name : 'selected resource'} to workflow steps
                  </p>
                </div>
              </div>
            </label>
          </div>
        )}
      </div>
    </div>
  );

  // Update the getAvailableResourcesForTimeSlot function to include buffer time in availability calculations
  const getAvailableResourcesForTimeSlot = (date: Date, timeString: string): Resource[] => {
    // Convert date to local timezone string
    const localDate = new Date(date);
    const offset = localDate.getTimezoneOffset();
    localDate.setMinutes(localDate.getMinutes() + offset);
    const dateStr = format(localDate, 'yyyy-MM-dd');
    
    // If we're still loading blocked slots, return empty array
    if (blockedSlotsLoading) return [];

    // Calculate total duration including buffer time for the new booking
    const serviceDurationMinutes = selectedServices.reduce((total, service) => {
      let duration = total + (service.priceOption.duration_minutes || 0);
      const addons = selectedServiceAddons[service.service.id] || [];
      duration += addons.reduce((addonSum, addon) => addonSum + (addon.duration_minutes || 0), 0);
      return duration;
    }, 0);
    
    // Add buffer time to total duration for scheduling purposes
    const totalDurationWithBuffer = serviceDurationMinutes + bufferTime;

    // Convert booking start time to minutes since midnight
    const [startHour, startMinute] = timeString.split(':').map(Number);
    const bookingStartMinutes = startHour * 60 + startMinute;
    const bookingEndMinutes = bookingStartMinutes + totalDurationWithBuffer;

    console.log('Checking availability with buffer time:', {
      serviceDuration: serviceDurationMinutes,
      bufferTime,
      totalDurationWithBuffer,
      startTime: timeString,
      endMinutes: bookingEndMinutes
    });

    const availableResources = mobileAvailability.filter(resource => {
      try {
        // Check if any time during the booking duration (including buffer) overlaps with blocked slots
        const resourceBlockedSlots = blockedTimeSlots[resource.resource_id];
        if (resourceBlockedSlots) {
          const dateEntry = resourceBlockedSlots.find(entry => entry.date === dateStr);
          if (dateEntry) {
            // Convert blocked slots to minute ranges
            const blockedRanges = dateEntry.blockedSlots.map(slot => {
              const [hour, minute] = slot.split(':').map(Number);
              const slotMinutes = hour * 60 + minute;
              return {
                start: slotMinutes,
                end: slotMinutes + 30 // Each slot is 30 minutes
              };
            });

            // Check if booking overlaps with any blocked range
            const hasOverlap = blockedRanges.some(range => {
              // Booking starts before blocked slot ends AND booking ends after blocked slot starts
              return (bookingStartMinutes < range.end && bookingEndMinutes > range.start);
            });

            if (hasOverlap) {
              return false;
            }
          }
        }

        const dayOfWeek = format(localDate, 'EEEE').toLowerCase();

        // Only check postal code availability if a postal code is provided
        if (postalCode && postalCode.trim() !== '') {
          let postalCodes = resource.postal_codes;
          if (typeof postalCodes === 'string') {
            try {
              postalCodes = JSON.parse(postalCodes);
            } catch (err) {
              if (isReschedule) console.log('❌ TimeSlot: Failed to parse postal codes for resource:', resource.id);
              // For reschedule mode, don't fail completely on parse errors
              if (!isReschedule) return false;
            }
          }
          
          const availablePostalCodes = postalCodes?.[dayOfWeek] || [];
          const enteredPostalCode = postalCode.toUpperCase().replace(/\s+/g, '');
          
          if (isReschedule) {
            console.log('📮 TimeSlot postal code check:', {
              resourceId: resource.id,
              dayOfWeek,
              enteredPostalCode,
              availablePostalCodesCount: availablePostalCodes.length,
              isMatch: availablePostalCodes.includes(enteredPostalCode)
            });
          }
          
          // For normal booking, strict postal code check
          if (!isReschedule && !availablePostalCodes.includes(enteredPostalCode)) {
            return false;
          }
          
          // For reschedule mode, we'll apply fallback logic later if needed
          if (isReschedule && !availablePostalCodes.includes(enteredPostalCode)) {
            // Mark this resource as postal-code-mismatched for potential fallback
            (resource as any)._postalCodeMismatch = true;
          }
        }

        // Check working hours
        let workingHours = resource.working_hours;
        if (typeof workingHours === 'string') {
          workingHours = JSON.parse(workingHours);
        }

        const daySchedule = workingHours?.[dayOfWeek];
        if (!daySchedule?.enabled || !daySchedule?.shifts?.length) {
          return false;
        }

        // Check if entire booking duration (including buffer) fits within any shift
        const isAvailable = daySchedule.shifts.some(shift => {
          const [shiftStartHour, shiftStartMinute] = shift.start.split(':').map(Number);
          const shiftStartMinutes = shiftStartHour * 60 + shiftStartMinute;

          const [shiftEndHour, shiftEndMinute] = shift.end.split(':').map(Number);
          const shiftEndMinutes = shiftEndHour * 60 + shiftEndMinute;
          
          return bookingStartMinutes >= shiftStartMinutes && bookingEndMinutes <= shiftEndMinutes;
        });

        return isAvailable;
      } catch (err) {
        console.error('Error checking resource availability:', err);
        return false;
      }
    }).map(resource => ({
      id: resource.id,                     // mobile_availability record ID
      resource_id: resource.resource_id,    // actual resource ID from resources table
      name: resourceNames[resource.id] || `Resource ${resource.resource_id}`,
      business_id: selectedBusiness?.id || '',
      mobile_available: true,
      working_hours: resource.working_hours,
      postal_codes: resource.postal_codes,
      created_at: resource.created_at,
      updated_at: resource.updated_at
    }));

    // Fallback for reschedule mode: if no resources are available due to postal code mismatch,
    // try again with only working hours check
    if (availableResources.length === 0 && isReschedule && postalCode && postalCode.trim() !== '') {
      console.log('🔄 TimeSlot fallback: No resources found, trying without postal code restriction');
      
      const fallbackResources = mobileAvailability.filter(resource => {
        try {
          // Check if any time during the booking duration (including buffer) overlaps with blocked slots
          const resourceBlockedSlots = blockedTimeSlots[resource.resource_id];
          if (resourceBlockedSlots) {
            const dateEntry = resourceBlockedSlots.find(entry => entry.date === dateStr);
            if (dateEntry) {
              // Convert blocked slots to minute ranges
              const blockedRanges = dateEntry.blockedSlots.map(slot => {
                const [hour, minute] = slot.split(':').map(Number);
                const slotMinutes = hour * 60 + minute;
                return {
                  start: slotMinutes,
                  end: slotMinutes + 30 // Each slot is 30 minutes
                };
              });

              // Check if booking overlaps with any blocked range
              const hasOverlap = blockedRanges.some(range => {
                // Booking starts before blocked slot ends AND booking ends after blocked slot starts
                return (bookingStartMinutes < range.end && bookingEndMinutes > range.start);
              });

              if (hasOverlap) {
                return false;
              }
            }
          }

          const dayOfWeek = format(localDate, 'EEEE').toLowerCase();

          // Skip postal code check for fallback
          
          // Check working hours only
          let workingHours = resource.working_hours;
          if (typeof workingHours === 'string') {
            workingHours = JSON.parse(workingHours);
          }

          const daySchedule = workingHours?.[dayOfWeek];
          if (!daySchedule?.enabled || !daySchedule?.shifts?.length) {
            return false;
          }

          // Check if entire booking duration (including buffer) fits within any shift
          const isAvailable = daySchedule.shifts.some(shift => {
            const [shiftStartHour, shiftStartMinute] = shift.start.split(':').map(Number);
            const shiftStartMinutes = shiftStartHour * 60 + shiftStartMinute;

            const [shiftEndHour, shiftEndMinute] = shift.end.split(':').map(Number);
            const shiftEndMinutes = shiftEndHour * 60 + shiftEndMinute;
            
            return bookingStartMinutes >= shiftStartMinutes && bookingEndMinutes <= shiftEndMinutes;
          });

          if (isAvailable) {
            console.log('✅ TimeSlot fallback: Found resource with working hours:', {
              resourceId: resource.id,
              dayOfWeek,
              timeString
            });
          }

          return isAvailable;
        } catch (err) {
          console.error('Error in TimeSlot fallback check:', err);
          return false;
        }
      }).map(resource => ({
        id: resource.id,
        resource_id: resource.resource_id,
        name: resourceNames[resource.id] || `Resource ${resource.resource_id}`,
        business_id: selectedBusiness?.id || '',
        mobile_available: true,
        working_hours: resource.working_hours,
        postal_codes: resource.postal_codes,
        created_at: resource.created_at,
        updated_at: resource.updated_at
      }));
      
      if (fallbackResources.length > 0) {
        console.log('✅ TimeSlot fallback successful: Found resources using working hours only');
        return fallbackResources;
      }
    }

    return availableResources;
  };

  // Update the generateTimeSlots function to handle timezone correctly
  const generateTimeSlots = (): string[] => {
    try {
      const slots: string[] = [];
      const startMinutes = 9 * 60; // 9 AM
      const endMinutes = 17 * 60; // 5 PM
      
      // Generate slots in 30-minute intervals
      for (let timeMinutes = startMinutes; timeMinutes <= endMinutes; timeMinutes += 30) {
        const hours = Math.floor(timeMinutes / 60);
        const minutes = timeMinutes % 60;
        const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        
        // Create a date object for the selected date at the current time slot
        const selectedDate = scheduleData.startDate 
          ? new Date(scheduleData.startDate + 'T12:00:00Z')
          : new Date();
        
        // Only add the slot if it hasn't passed (for today) or it's a future date
        if (!hasTimePassed(selectedDate, timeString)) {
          slots.push(timeString);
        }
      }

      console.log('Generated time slots:', slots);
      return slots;
    } catch (err) {
      console.error('Error generating time slots:', err);
      return [];
    }
  };

  // Update the location mode handler to use the new prop
  const handleLocationModeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    console.log("Switching from mobile to location booking");
    // If we have the onSwitchToLocation prop, use it
    if (onSwitchToLocation) {
      onSwitchToLocation();
    } else {
      // Otherwise, just close the modal (fallback to old behavior)
      onClose();
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <>
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 create-mobile-booking-modal ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
        } z-[100000]`}
        onClick={onClose}
        style={{
          touchAction: 'manipulation',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        }}
      >
        <div 
          className={`fixed inset-y-0 right-0 w-full md:w-[600px] backdrop-blur-xl bg-white/80 transform transition-transform duration-300 ease-out shadow-2xl ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          } z-[100001]`}
          style={{ 
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)'
          }}
          onTransitionEnd={handleTransitionEnd}
          onClick={e => e.stopPropagation()}
        >
          <div className="h-full flex flex-col">
            {/* Header - Clean Modern Design */}
            <div className="bg-gray-900 border-b border-gray-800">
              <div className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                  <h2 className="text-xl font-semibold text-white">
                    Create Mobile Booking
                  </h2>
                </div>
                {onSwitchToLocation && (
                  <div className="flex bg-gray-800 rounded-lg p-1 border border-gray-700">
                    <button
                      type="button"
                      onClick={handleLocationModeClick}
                      className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
                    >
                      Location
                    </button>
                    <button
                      type="button"
                      className="px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-md"
                    >
                      Mobile
                    </button>
                  </div>
                )}
              </div>
            </div>

            <form 
              onSubmit={handleSubmit} 
              className="flex-1 overflow-y-auto bg-gray-50"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="space-y-0">
                {error && (
                  <div className="bg-red-50 border-b border-red-200 text-red-700 px-6 py-4 text-sm">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {error}
                    </div>
                  </div>
                )}

                {/* Title Section */}
                <div className="bg-white border-b border-gray-200 p-6">
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter booking title..."
                    style={{ fontSize: '16px' }}
                  />
                </div>



                {/* Customer Source Dropdown Portal */}
                {isCustomerSourceDropdownOpen && customerSourceButtonRef.current && createPortal(
                  <>
                    <div 
                      className="fixed inset-0 z-[100002]" 
                      onClick={() => setIsCustomerSourceDropdownOpen(false)}
                    />
                    <div 
                      className="fixed z-[100003] bg-white/95 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl max-h-60 overflow-y-auto"
                      style={{ 
                        backdropFilter: 'blur(20px)', 
                        WebkitBackdropFilter: 'blur(20px)',
                        left: customerSourceButtonRef.current.getBoundingClientRect().left,
                        top: customerSourceButtonRef.current.getBoundingClientRect().bottom + 4,
                        width: customerSourceButtonRef.current.getBoundingClientRect().width,
                        maxWidth: '400px'
                      }}
                    >
                      <div 
                        onClick={() => {
                          setSelectedCustomerSource(null);
                          setIsCustomerSourceDropdownOpen(false);
                        }}
                        className="px-4 py-3 text-sm text-gray-500 hover:bg-purple-50/50 cursor-pointer transition-colors duration-150"
                      >
                        No source selected
                      </div>
                      
                      {/* Hardcoded Repeat Customer Source */}
                      <div
                        onClick={() => {
                          setSelectedCustomerSource({ 
                            id: 'repeat-customer', 
                            business_id: selectedBusiness?.id || '',
                            name: 'Repeat Customer', 
                            is_active: true,
                            created_at: '',
                            updated_at: ''
                          });
                          setIsCustomerSourceDropdownOpen(false);
                        }}
                        className="px-4 py-3 text-sm text-gray-900 hover:bg-purple-50/50 cursor-pointer transition-colors duration-150 border-t border-gray-100/50 flex items-center justify-between"
                      >
                        <span>Repeat Customer</span>
                        <span className="text-xs text-green-600 font-medium">System</span>
                      </div>
                      
                      {/* Regular Customer Sources */}
                      {customerSources.map((source) => (
                        <div
                          key={source.id}
                          onClick={() => {
                            setSelectedCustomerSource(source);
                            setIsCustomerSourceDropdownOpen(false);
                          }}
                          className="px-4 py-3 text-sm text-gray-900 hover:bg-purple-50/50 cursor-pointer transition-colors duration-150 border-t border-gray-100/50"
                        >
                          {source.name}
                        </div>
                      ))}
                      
                      {/* Add Source Option */}
                      <div
                        onClick={() => {
                          setIsCustomerSourceDropdownOpen(false);
                          setIsCreateSourceModalOpen(true);
                        }}
                        className="px-4 py-3 text-sm text-purple-600 hover:bg-purple-50/50 cursor-pointer transition-colors duration-150 border-t border-gray-100/50 flex items-center space-x-2"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add Source</span>
                      </div>
                    </div>
                  </>,
                  document.body
                )}

                {/* Customer Section */}
                <div className="bg-white border-b border-gray-200 p-6">
                  {selectedCustomer ? (
                    <div className="space-y-4">
                      {/* Customer Display */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-sm">
                            {selectedCustomer.first_name[0]}{selectedCustomer.last_name[0]}
                          </div>
                          <div>
                            <div className="text-base font-medium text-gray-900">
                              {selectedCustomer.first_name} {selectedCustomer.last_name}
                            </div>
                            <div className="flex flex-col space-y-1 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4 text-sm text-gray-600">
                              {selectedCustomer.phone && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="h-4 w-4 text-gray-400" />
                                  <span className="truncate">{selectedCustomer.phone}</span>
                                </div>
                              )}
                              {selectedCustomer.email && (
                                <div className="flex items-center space-x-2">
                                  <Mail className="h-4 w-4 text-gray-400" />
                                  <span className="truncate">{selectedCustomer.email}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Customer Source */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Customer Source</label>
                        <button
                          ref={customerSourceButtonRef}
                          type="button"
                          onClick={() => setIsCustomerSourceDropdownOpen(!isCustomerSourceDropdownOpen)}
                          className={`w-full flex items-center justify-between px-4 py-3 text-base bg-white rounded-lg focus:outline-none transition-colors ${
                            selectedCustomerSource 
                              ? "border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                              : "border-2 border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          }`}
                          style={{ fontSize: '16px' }}
                        >
                          <span className={selectedCustomerSource ? "text-gray-900" : "text-red-600"}>
                            {selectedCustomerSource ? selectedCustomerSource.name : "No source selected"}
                          </span>
                          <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isCustomerSourceDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                      </div>

                      {/* Notes */}
                      <div>
                        <textarea
                          value={scheduleData.customer_notes || ''}
                          onChange={(e) => setScheduleData(prev => ({
                            ...prev,
                            customer_notes: e.target.value
                          }))}
                          placeholder="Add booking notes..."
                          className="w-full h-24 px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                          style={{ fontSize: '16px' }}
                        />
                      </div>

                      {/* Booking Address */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Booking Address
                          </label>
                          {bookingAddress && selectedCustomer?.address && (
                            <button
                              type="button"
                              onClick={() => {
                                // Reset to customer's address
                                if (selectedCustomer?.address) {
                                  setBookingAddress(selectedCustomer.address);
                                  // Extract postal code from customer's address
                                  const postalCodeRegex = /[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d/i;
                                  const match = selectedCustomer.address.match(postalCodeRegex);
                                  if (match) {
                                    const fullPostalCode = match[0].replace(/\s+/g, '').toUpperCase();
                                    setPostalCode(fullPostalCode.substring(0, 3)); // Use first 3 characters for area code
                                    console.log(`Extracted postal code "${fullPostalCode}" from customer address, using FSA: ${fullPostalCode.substring(0, 3)}`);
                                  } else {
                                    console.log('No postal code found in customer address:', selectedCustomer.address);
                                  }
                                }
                              }}
                              className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              Reset to Customer Address
                            </button>
                          )}
                        </div>
                        <AddressAutocompleteModal
                          value={bookingAddress}
                          onChange={(value) => {
                            console.log('Address changed:', value);
                            setBookingAddress(value);
                          }}
                          onSelect={(address) => {
                            console.log('Address selected from Google Autocomplete:', address);
                            setBookingAddress(address.formatted);
                            if (address.postalCode) {
                              const cleanPostalCode = address.postalCode.replace(/\s+/g, '').toUpperCase();
                              console.log(`Setting postal code from Google Autocomplete: ${cleanPostalCode} (Full), ${cleanPostalCode.substring(0, 3)} (FSA)`);
                              setPostalCode(cleanPostalCode.substring(0, 3));
                            } else {
                              console.log('No postal code returned from Google Autocomplete');
                              // Try to extract from formatted address as fallback
                              const postalCodeRegex = /[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d/i;
                              const match = address.formatted.match(postalCodeRegex);
                              if (match) {
                                const extractedPostalCode = match[0].replace(/\s+/g, '').toUpperCase();
                                console.log(`Extracted postal code ${extractedPostalCode} from formatted address as fallback`);
                                setPostalCode(extractedPostalCode.substring(0, 3));
                              }
                            }
                          }}
                          placeholder="Enter service location address"
                          className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"

                        />
                      </div>

                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Customer *
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsSelectCustomerModalOpen(true)}
                        className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <Users className="h-5 w-5 mr-2" />
                        Select Customer
                      </button>
                    </div>
                  )}
                </div>



                {/* Services Section */}
                {selectedCustomer && (
                  <div className="bg-white border-b border-gray-200 p-6">
                    {/* Group services by vehicle */}
                    {Object.entries(servicesByVehicle).map(([vehicleId, services]) => {
                      const vehicle = services[0].vehicle; // Get vehicle info from first service
                      return (
                        <div key={vehicleId} className="mb-3">
                          <div className="bg-white rounded-lg border border-gray-200 p-4">
                            {/* Vehicle Header - Clickable to Edit */}
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedVehicleToEdit(vehicle);
                                setIsEditVehicleModalOpen(true);
                              }}
                              className="w-full flex items-center justify-between mb-2 pb-2 border-b border-gray-100 hover:bg-gray-50/50 transition-all duration-200 rounded-lg px-2 py-1 -mx-2 -my-1"
                            >
                              <div className="text-sm font-medium text-gray-900">
                                {vehicle.year} {vehicle.make} {vehicle.model}
                              </div>
                              <Car className="h-4 w-4 text-gray-400" />
                            </button>

                            {/* Services List */}
                            <div className="space-y-3">
                              {services.map((item, serviceIndex) => (
                                <div key={`${item.service.id}-${serviceIndex}`} className="pb-2 border-b border-gray-50">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <div className="text-sm text-gray-900">{item.service.name}</div>
                                        <div className="text-sm font-medium text-gray-900">${item.priceOption.price.toFixed(2)}</div>
                                      </div>
                                      <div className="mt-1 flex items-center text-xs text-gray-500">
                                        <Clock className="h-3.5 w-3.5 mr-1" />
                                        {(() => {
                                          // Calculate total duration for this service (including addons but without buffer)
                                          const serviceDuration = item.priceOption.duration_minutes || 0;
                                          const addons = selectedServiceAddons[item.service.id] || [];
                                          const addonDuration = addons.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0);
                                          const totalDuration = serviceDuration + addonDuration;
                                          
                                          if (totalDuration >= 1440) {
                                            return `${Math.round(totalDuration / 1440)} days`;
                                          } else if (totalDuration >= 60) {
                                            const hours = Math.floor(totalDuration / 60);
                                            const minutes = totalDuration % 60;
                                            return minutes > 0 ? `${hours} hours ${minutes} mins` : `${hours} hours`;
                                          } else {
                                            return `${totalDuration} mins`;
                                          }
                                        })()}
                                      </div>
                                    </div>
                                    <div className="ml-4 flex items-center space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setCurrentService(item.service);
                                          setIsSelectAddonsModalOpen(true);
                                        }}
                                        className="text-xs text-green-600 hover:text-green-800"
                                      >
                                        Add-ons
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedServiceToEdit({
                                            vehicle,
                                            service: item.service,
                                            priceOption: item.priceOption
                                          });
                                          setIsEditServiceModalOpen(true);
                                        }}
                                        className="text-xs text-blue-600 hover:text-blue-800"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveService(vehicle.id, item.service.id)}
                                        className="text-xs text-red-600 hover:text-red-800"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                  {/* Service Add-ons */}
                                  {selectedServiceAddons[item.service.id]?.map(addon => (
                                    <div key={addon.id} className="mt-2 pl-4 border-l-2 border-gray-100">
                                      <div className="flex items-start justify-between">
                                        <div>
                                          <div className="text-sm text-gray-900">{addon.name}</div>
                                          <div className="text-xs text-gray-500">${addon.price.toFixed(2)}</div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ))}
                              
                              {/* Add Service Button for this specific vehicle */}
                              <button
                                type="button"
                                onClick={() => {
                                  console.log('🔍 DEBUG: Add Service button clicked for vehicle:', vehicle);
                                  console.log('🔍 DEBUG: Setting currentVehicle to:', vehicle);
                                  setCurrentVehicle(vehicle);
                                  console.log('🔍 DEBUG: Setting SelectServiceModal to open');
                                  setIsSelectServiceModalOpen(true);
                                  console.log('🔍 DEBUG: isSelectServiceModalOpen should now be true');
                                }}
                                className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Service to {vehicle.year} {vehicle.make} {vehicle.model}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <div className="space-y-3">
                      {/* Add Service Button - only show when no services exist yet */}
                      {selectedServices.length === 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            console.log('🔍 DEBUG: Main Add Service button clicked');
                            console.log('🔍 DEBUG: selectedCustomer:', selectedCustomer);
                            console.log('🔍 DEBUG: selectedCustomer?.vehicles?.length:', selectedCustomer?.vehicles?.length);
                            
                            if (!selectedCustomer?.vehicles?.length) {
                              console.log('🔍 DEBUG: No vehicles found, opening CreateVehicleModal');
                              setIsCreateVehicleModalOpen(true);
                              return;
                            }
                            console.log('🔍 DEBUG: Vehicles found, opening SelectVehicleModal');
                            // Start with vehicle selection since no services exist
                            setIsSelectVehicleModalOpen(true);
                          }}
                          className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          <Plus className="h-5 w-5 mr-2" />
                          Add Service
                        </button>
                      )}

                      {/* Add Another Vehicle Button - always show when customer has vehicles */}
                      {selectedCustomer?.vehicles && selectedCustomer.vehicles.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setCurrentVehicle(null);
                            setIsSelectVehicleModalOpen(true);
                          }}
                          className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          <Car className="h-5 w-5 mr-2" />
                          Add Another Vehicle
                        </button>
                      )}

                      {/* Create Vehicle Button - show when customer has no vehicles */}
                      {selectedCustomer && (!selectedCustomer.vehicles || selectedCustomer.vehicles.length === 0) && (
                        <button
                          type="button"
                          onClick={() => setIsCreateVehicleModalOpen(true)}
                          className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          <Plus className="h-5 w-5 mr-2" />
                          Create Vehicle & Add Service
                        </button>
                      )}
                    </div>
                  </div>
                )}





                {/* Quick Booking Details */}
                {prefilledData?.isQuickBooking && (
                  <div className="bg-white border-b border-gray-200 p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Quick Booking Details</h3>
                        <p className="text-sm text-gray-600">Pre-selected time slot</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">Resource:</span>
                        <span className="text-sm font-semibold text-blue-700">{prefilledData.resource?.name}</span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">Date:</span>
                        <span className="text-sm font-semibold text-blue-700">
                          {formatReadableDate(prefilledData.scheduleData?.startDate || '')}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">Time:</span>
                        <span className="text-sm font-semibold text-blue-700">
                          {formatReadableTime(prefilledData.scheduleData?.startTime || '')}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">Available Time:</span>
                        <span className="text-sm font-semibold text-blue-700">
                          {prefilledData.scheduleData?.availableHours || 0} hours
                        </span>
                      </div>
                      
                      {selectedServices.length > 0 && (
                        <>
                          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200/30">
                            <span className="text-sm font-medium text-gray-700">Service Duration:</span>
                            <span className="text-sm font-semibold text-purple-700">
                              {totalDurationMinutes >= 1440 
                                ? `${Math.floor(totalDurationMinutes / 1440)} days` 
                                : totalDurationMinutes >= 60
                                  ? `${Math.floor(totalDurationMinutes / 60)} hours ${totalDurationMinutes % 60 > 0 ? `${totalDurationMinutes % 60} mins` : ''}`
                                  : `${totalDurationMinutes} mins`}
                            </span>
                          </div>
                          
                          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200/30">
                            <span className="text-sm font-medium text-gray-700">Duration with Buffer:</span>
                            <span className="text-sm font-semibold text-purple-700">
                              {(totalDurationMinutes + bufferTime) >= 1440 
                                ? `${Math.floor((totalDurationMinutes + bufferTime) / 1440)} days` 
                                : (totalDurationMinutes + bufferTime) >= 60
                                  ? `${Math.floor((totalDurationMinutes + bufferTime) / 60)} hours ${(totalDurationMinutes + bufferTime) % 60 > 0 ? `${(totalDurationMinutes + bufferTime) % 60} mins` : ''}`
                                  : `${totalDurationMinutes + bufferTime} mins`}
                            </span>
                          </div>
                          
                          {(() => {
                            const availableHours = prefilledData.scheduleData?.availableHours || 0;
                            const durationWithBufferHours = (totalDurationMinutes + bufferTime) / 60;
                            const isDurationValid = durationWithBufferHours <= availableHours;
                            
                            console.log('🔍 Quick booking duration check:', {
                              selectedServicesCount: selectedServices.length,
                              totalDurationMinutes,
                              bufferTime,
                              durationWithBufferHours,
                              availableHours,
                              isDurationValid
                            });
                            
                            return (
                              <div className={`flex items-center justify-between p-3 rounded-xl border ${
                                isDurationValid 
                                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200/30' 
                                  : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-200/30'
                              }`}>
                                <span className="text-sm font-medium text-gray-700">Time Check:</span>
                                <span className={`text-sm font-semibold ${
                                  isDurationValid ? 'text-green-700' : 'text-red-700'
                                }`}>
                                  {isDurationValid ? '✓ Fits in time slot' : '✗ Exceeds available time'}
                                </span>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Calendar and Time Selection - Hide for quick booking */}
                {selectedServices.length > 0 && !prefilledData?.isQuickBooking && (
                  <div className="bg-white border-b border-gray-200 p-6">
                    {renderCalendar()}
                  </div>
                )}

                {/* Payment Summary */}
                {selectedServices.length > 0 && (
                  <div className="bg-white border-b border-gray-200 p-6">
                    {renderPaymentSummary()}
                  </div>
                )}

                {/* Submit Button */}
                <div className="bg-white border-t border-gray-200 px-6 py-4">
                  <button
                    type="submit"
                    disabled={(() => {
                      const basicChecks = loading || !selectedCustomer || selectedServices.length === 0;
                      
                      // For quick booking, also check if duration with buffer exceeds available time
                      if (prefilledData?.isQuickBooking && selectedServices.length > 0) {
                        const availableHours = prefilledData.scheduleData?.availableHours || 0;
                        const durationWithBufferHours = (totalDurationMinutes + bufferTime) / 60;
                        const durationExceedsAvailable = durationWithBufferHours > availableHours;
                        
                        return basicChecks || durationExceedsAvailable;
                      }
                      
                      return basicChecks;
                    })()}
                    className="w-full px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        {prefilledData?.isQuickBooking ? 'Creating Quick Booking...' : 'Creating Mobile Booking...'}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <Smartphone className="h-4 w-4 mr-2" />
                        {prefilledData?.isQuickBooking ? 'Create Quick Booking' : 'Create Mobile Booking'}
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {createPortal(
        <SelectCustomerModal
          isOpen={isSelectCustomerModalOpen}
          onClose={() => setIsSelectCustomerModalOpen(false)}
          onSelect={customer => {
            console.log('📱 Customer selected in CreateMobileBookingModal:', customer);
            setSelectedCustomer(customer);
            setCurrentVehicle(null);
            setIsSelectCustomerModalOpen(false);
            
            // Auto-detect customer source based on repeat status
            detectCustomerSource(customer);
            
            // Save progress immediately with the new customer data
            saveMobileBookingProgress({ selectedCustomer: customer });
          }}
        />,
        document.body
      )}

      {createPortal(
        <SelectVehicleModal
          isOpen={isSelectVehicleModalOpen}
          onClose={() => setIsSelectVehicleModalOpen(false)}
          onSelect={handleSelectVehicle}
          vehicles={selectedCustomer?.vehicles || []}
          selectedVehicles={selectedServices.map(s => s.vehicle)}
          onAddVehicle={() => {
            setIsSelectVehicleModalOpen(false);
            setIsCreateVehicleModalOpen(true);
          }}
        />,
        document.body
      )}

      {createPortal(
        <CreateVehicleModal
          isOpen={isCreateVehicleModalOpen}
          onClose={() => setIsCreateVehicleModalOpen(false)}
          onSuccess={(vehicle: any) => handleVehicleCreated(vehicle)}
          customerId={selectedCustomer?.id || ''}
        />,
        document.body
      )}

      {createPortal(
        <EditCustomerModal
          isOpen={isEditCustomerModalOpen}
          onClose={() => setIsEditCustomerModalOpen(false)}
          onSuccess={(updatedCustomer) => {
            setIsEditCustomerModalOpen(false);
            setSelectedCustomer(updatedCustomer);
          }}
          customer={selectedCustomer}
        />,
        document.body
      )}

      {createPortal(
        <SelectServiceModal
          isOpen={isSelectServiceModalOpen}
          onClose={() => setIsSelectServiceModalOpen(false)}
          onAdd={async (service, priceOption) => {
            handleAddService(service, priceOption);
            return Promise.resolve();
          }}
        />,
        document.body
      )}

      {createPortal(
        <EditServiceModal
          isOpen={isEditServiceModalOpen}
          onClose={() => {
            setIsEditServiceModalOpen(false);
                setSelectedServiceToEdit(null);
              }}
              onSave={async (updatedService) => {
                if (!selectedServiceToEdit) return;
                
                // Update the service in the selectedServices array
                setSelectedServices(prev => prev.map(s => 
                  s.vehicle.id === selectedServiceToEdit.vehicle.id && s.service.id === selectedServiceToEdit.service.id
                    ? {
                        ...s,
                        service: {
                          ...s.service,
                          name: updatedService.name,
                          description: updatedService.description,
                          tax_exempt: updatedService.tax_exempt,
                          tax_rate: updatedService.tax_rate
                        },
                        priceOption: {
                          ...s.priceOption,
                          price: updatedService.price,
                          duration_minutes: updatedService.duration_minutes
                        }
                      }
                    : s
                ));

                // Update the add-ons for this service
                if (updatedService.addons) {
                  setSelectedServiceAddons(prev => ({
                    ...prev,
                    [selectedServiceToEdit.service.id]: updatedService.addons
                  }));
                }

                setIsEditServiceModalOpen(false);
                setSelectedServiceToEdit(null);
              }}
              service={selectedServiceToEdit ? {
                name: selectedServiceToEdit.service.name,
                description: selectedServiceToEdit.service.description,
                price: selectedServiceToEdit.priceOption.price,
                duration_minutes: selectedServiceToEdit.priceOption.duration_minutes,
                is_active: true,
                tax_rate: selectedServiceToEdit.service.tax_rate || 5,
                tax_exempt: selectedServiceToEdit.service.tax_exempt || false,
                addons: selectedServiceAddons[selectedServiceToEdit.service.id] || []
              } : null}
            />,
            document.body
          )}

      {createPortal(
        <EditVehicleModal
          isOpen={isEditVehicleModalOpen}
          onClose={() => {
            setIsEditVehicleModalOpen(false);
            setSelectedVehicleToEdit(null);
          }}
          vehicle={selectedVehicleToEdit}
          onUpdate={async (updatedVehicle: CustomerVehicle) => {
            // Update customer data to get the updated vehicle
            const updatedCustomer = await getCustomer(selectedCustomer?.id || '');
            setSelectedCustomer(updatedCustomer);
            
            // Update the vehicle in selectedServices
            setSelectedServices(prev => prev.map(s => 
              s.vehicle.id === updatedVehicle.id
                ? { ...s, vehicle: updatedVehicle }
                : s
            ));
            
            setIsEditVehicleModalOpen(false);
            setSelectedVehicleToEdit(null);
          }}
        />,
        document.body
      )}

      <SelectServiceAddonsModal
        isOpen={isSelectAddonsModalOpen}
        onClose={() => {
          setIsSelectAddonsModalOpen(false);
          setCurrentService(null);
        }}
        serviceId={currentService?.id || ''}
        onSelect={handleAddAddons}
        selectedAddons={currentService ? selectedServiceAddons[currentService.id] || [] : []}
      />

      {isResourceSelectorOpen && (
        <div className="fixed inset-0 z-[100008]" style={{pointerEvents: isResourceSelectorOpen ? 'auto' : 'none'}}>
          <ResourceSelector />
        </div>
      )}

      {/* Create Customer Source Modal */}
      {isCreateSourceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100006]">
          <div className="backdrop-blur-xl bg-white/90 rounded-2xl shadow-xl p-6 border border-white/20 w-full max-w-md mx-4"
               style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Add Customer Source
              </h3>
              <button
                onClick={() => {
                  setIsCreateSourceModalOpen(false);
                  setNewSourceName('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source Name
              </label>
              <input
                type="text"
                value={newSourceName}
                onChange={(e) => setNewSourceName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Enter source name (e.g., Google Ads, Referral)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newSourceName.trim()) {
                    handleCreateCustomerSource();
                  }
                }}
                autoFocus
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setIsCreateSourceModalOpen(false);
                  setNewSourceName('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={createSourceLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCustomerSource}
                disabled={!newSourceName.trim() || createSourceLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createSourceLoading ? 'Creating...' : 'Create Source'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
} 